/**
 * NotificationBell — Facebook-style bell + dropdown panel.
 *
 * Renders in the global navbar. Polls every 30 seconds for new rows from
 * GET /api/me/notifications. Clicking the bell toggles a panel listing the
 * most recent 30 notifications, with "Mark all read" + "Clear all" actions.
 *
 * Click on a wave-related notification → navigates to /panelist (where the
 * patron sees the survey card). Survey-completed notifications are also
 * informational only — clicking marks them read.
 */
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { Bell } from "lucide-react";
import { toast } from "sonner";
import { formatDate } from "../lib/date";
import api from "../lib/api";
import { useAuth } from "../lib/auth";

function relTime(iso) {
  if (!iso) return "";
  const t = new Date(iso).getTime();
  const diff = Math.floor((Date.now() - t) / 1000);
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return formatDate(iso);
}

export default function NotificationBell({ adminMode = false }) {
  const { user } = useAuth();
  const nav = useNavigate();
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState(null);  // {title, body_md, sender_name, created_at} when a "Read more" is clicked
  const btnRef = useRef(null);       // the bell button
  const panelRef = useRef(null);     // the dropdown panel (rendered via portal)
  // Viewport-anchored position for the portal-rendered dropdown. We compute
  // this from the bell's getBoundingClientRect() at open time (and on scroll
  // / resize while open) so the dropdown lives outside every parent
  // overflow-clipping context but still lines up under the bell.
  const [pos, setPos] = useState({ top: 0, right: 8, width: 320 });

  const load = () => {
    if (!user) return;
    api.get("/me/notifications").then((r) => setRows(r.data || [])).catch(() => {});
  };

  useEffect(() => {
    if (!user) return undefined;
    load();
    const id = setInterval(load, 30000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  // Compute panel position from the bell's viewport rect. Runs on open,
  // and re-runs on scroll/resize while open so the panel stays anchored.
  useLayoutEffect(() => {
    if (!open) return undefined;
    const reposition = () => {
      const b = btnRef.current?.getBoundingClientRect();
      if (!b) return;
      // Panel width — matches the CSS w-80 (320px), but clamped to the
      // viewport so on very narrow phones (~360px) the panel never gets
      // wider than the screen minus a small 8px gutter on each side.
      const PANEL_W = Math.min(320, window.innerWidth - 16);
      // Prefer to anchor the panel's right edge under the bell. If that
      // would push the panel's left edge past the left viewport gutter
      // (8px), slide the panel leftward until it fits.
      const desiredRight = Math.round(window.innerWidth - b.right);
      const maxRight = window.innerWidth - PANEL_W - 8;   // keeps left ≥ 8px
      const right = Math.max(8, Math.min(desiredRight, maxRight));
      setPos({
        top: Math.round(b.bottom + 8),                    // 8px = mt-2
        right,
        width: PANEL_W,
      });
    };
    reposition();
    window.addEventListener("scroll", reposition, true);
    window.addEventListener("resize", reposition);
    return () => {
      window.removeEventListener("scroll", reposition, true);
      window.removeEventListener("resize", reposition);
    };
  }, [open]);

  // Click-outside close — checks both the bell button AND the portal panel
  // (panel lives outside the button's DOM subtree because of the portal).
  useEffect(() => {
    const onDown = (e) => {
      if (btnRef.current?.contains(e.target)) return;
      if (panelRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    if (open) document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  if (!user) return null;

  // Set of wave IDs the patron has already completed — used to mark
  // wave-related notifications visibly and to short-circuit the click
  // handler to a toast instead of dropping the user on a dead-end survey
  // page.
  const completedCache = api.getCached("/me/completed-waves") || [];
  const completedSet = new Set(completedCache.map((row) => row.wave?.id || row.id || row.wave_id).filter(Boolean));
  const isWaveNotif = (n) => n.kind === "wave_available" || n.kind === "survey_invite";
  const isAlreadyDone = (n) => isWaveNotif(n) && n.ref_id && completedSet.has(n.ref_id);

  const unread = rows.filter((n) => !n.read_at);
  const unreadCount = unread.length;

  const handleClick = async (n) => {
    if (!n.read_at) {
      try { await api.post(`/me/notifications/${n.id}/read`); } catch (_) { /* non-fatal */ }
    }
    // Already-submitted wave notifications stay strikethrough in the
    // dropdown — clicking does nothing (no nav, no toast, dropdown stays
    // open). Per product spec: the strike + SUBMITTED pill is enough.
    if (isAlreadyDone(n)) { load(); return; }
    setOpen(false);
    if (n.kind === "wave_available" || n.kind === "survey_invite") {
      if (n.ref_id) nav(`/panelist/survey/${n.ref_id}`);
      else nav("/panelist");
    }
    if (n.kind === "sentiment_rerate" && n.ref_id) {
      nav(`/panelist/sentiment/${n.ref_id}`);
    }
    load();
  };

  const markAllRead = async () => {
    try { await api.post("/me/notifications/read-all"); } catch (_) { /* non-fatal */ }
    load();
  };

  const clearAll = async () => {
    if (!window.confirm("Clear all notifications? This can't be undone.")) return;
    try { await api.delete("/me/notifications/clear-all"); } catch (_) { /* non-fatal */ }
    load();
  };

  return (
    <div className="relative">
      <button
        ref={btnRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label={`Notifications${unreadCount > 0 ? ` (${unreadCount} unread)` : ""}`}
        data-testid="notification-bell"
        className="relative p-2 hover:bg-neutral-100 transition-colors"
      >
        <Bell size={20} className={adminMode ? "text-white" : "text-black"} />
        {unreadCount > 0 && (
          <span
            data-testid="notification-bell-badge"
            className="absolute top-1 right-1 min-w-[18px] h-[18px] px-1 bg-mango text-white text-[10px] font-bold flex items-center justify-center rounded-full border-2 border-white"
          >
            {unreadCount > 99 ? "99+" : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown is portaled to <body> so it escapes every parent
          overflow-clipping context. The desktop nav wrapper uses
          `overflow-x-auto` to horizontally scroll a crowded menu on
          narrow desktops, which per CSS spec silently forces
          `overflow-y: auto` too — clipping this dropdown that extends
          below the nav's height. Portaling sidesteps the whole issue. */}
      {open && createPortal(
        <div
          ref={panelRef}
          data-testid="notification-dropdown"
          style={{ position: "fixed", top: pos.top, right: pos.right, width: pos.width }}
          className="max-h-[480px] bg-white border-2 border-black shadow-xl z-[100] flex flex-col"
        >
          <div className="flex items-center justify-between px-4 py-3 border-b-2 border-black bg-neutral-50">
            <div className="font-black text-sm uppercase tracking-wider">Notifications</div>
            <div className="flex items-center gap-2 text-[11px]">
              {unreadCount > 0 && (
                <button onClick={markAllRead} data-testid="notif-mark-read" className="underline text-neutral-700 hover:text-black">Mark all read</button>
              )}
              {rows.length > 0 && (
                <button onClick={clearAll} data-testid="notif-clear-all" className="underline text-red-700 hover:text-red-900">Clear all</button>
              )}
            </div>
          </div>
          <div className="overflow-y-auto flex-1">
            {rows.length === 0 ? (
              <div className="p-8 text-center text-xs text-neutral-500 italic">No notifications yet.</div>
            ) : rows.slice(0, 30).map((n) => (
              <button
                key={n.id}
                onClick={() => handleClick(n)}
                data-testid={`notif-${n.id}`}
                className={`w-full text-left px-4 py-3 border-b border-neutral-100 hover:bg-neutral-50 ${!n.read_at ? "bg-mango/5" : ""}`}
              >
                <div className="flex items-start gap-2">
                  {!n.read_at && <div className="w-2 h-2 bg-mango rounded-full mt-1.5 flex-shrink-0" />}
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-bold flex items-start gap-2 flex-wrap">
                      <span className={`min-w-0 break-words ${isAlreadyDone(n) ? "line-through text-neutral-400" : ""}`}>{n.title}</span>
                      {isAlreadyDone(n) && (
                        <span className="inline-flex items-center px-1.5 py-0.5 bg-emerald-100 text-emerald-700 text-[9px] font-bold tracking-wide uppercase border border-emerald-300 flex-shrink-0" data-testid={`notif-done-${n.id}`}>Submitted</span>
                      )}
                    </div>
                    {n.body && <div className="text-xs text-neutral-600 mt-0.5 break-words">{n.body}</div>}
                    {n.kind === "admin_message" && n.body_md && n.body_md.length > 0 && (
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); setExpanded(n); if (!n.read_at) { api.post(`/me/notifications/${n.id}/read`).catch(() => {}); load(); } }}
                        className="text-[11px] text-mango hover:underline font-semibold mt-1"
                        data-testid={`notif-readmore-${n.id}`}
                      >
                        Read more →
                      </button>
                    )}
                    <div className="text-[10px] text-neutral-400 mt-1">{relTime(n.created_at)}</div>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>,
        document.body
      )}

      {/* Full-message dialog opened from a "Read more" click. Renders the
          same branded card the patron received in their email (header,
          subject, paragraphs) so the in-app version matches what they got
          in their inbox. No footer — same as the email template. */}
      {expanded && (
        <div
          className="fixed inset-0 bg-black/60 z-[200] flex items-start justify-center overflow-y-auto p-4"
          onClick={() => setExpanded(null)}
          data-testid="notif-expanded-overlay"
        >
          <div
            className="bg-white border-2 border-black max-w-xl w-full my-12 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
            data-testid="notif-expanded-dialog"
          >
            <div className="bg-black text-white px-6 py-4 flex items-center justify-between">
              <div className="font-black tracking-tighter text-xl" style={{ fontFamily: "'IBM Plex Sans', system-ui, sans-serif" }}>oodel<span style={{ color: "#16A34A" }}>.</span></div>
              <button type="button" onClick={() => setExpanded(null)} className="text-xs underline opacity-80 hover:opacity-100" data-testid="notif-expanded-close">Close</button>
            </div>
            <div className="px-6 py-5">
              <div className="overline text-[10px] text-neutral-500">Hi {user?.name || "there"},</div>
              <h2 className="text-xl sm:text-2xl font-black tracking-tighter mt-1 mb-4">{expanded.title}</h2>
              <div className="text-sm leading-relaxed space-y-3 text-neutral-800 whitespace-pre-wrap break-words font-serif" data-testid="notif-expanded-body">
                {String(expanded.body_md || "").split(/\n\n+/).map((para, i) => (
                  <p key={i}>{para}</p>
                ))}
              </div>
              <p className="text-sm text-neutral-600 mt-6">— {expanded.sender_name || "The oodel. team"}</p>
              <div className="text-[10px] text-neutral-400 mt-4">{relTime(expanded.created_at)}</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
